import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquare, Send } from 'lucide-react';
import { getOrCreateProposalToken, buildProposalCustomerLink } from '../lib/proposals';
import { loadProposalSendMessage } from '../lib/proposalSendDefaults';

interface ProposalSMSModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (message: string) => Promise<void>;
  proposalTitle: string;
  businessName: string;
  customerPhone: string;
  customerFirstName: string;
  proposalId: string;
  businessId: string;
}

export default function ProposalSMSModal({
  isOpen,
  onClose,
  onSend,
  proposalTitle,
  businessName,
  customerPhone,
  customerFirstName,
  proposalId,
  businessId
}: ProposalSMSModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [proposalUrl, setProposalUrl] = useState('');

  // Set default values when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      
      // Generate proposal URL + load default or custom SMS template
      const generateUrl = async () => {
        try {
          const tokenData = await getOrCreateProposalToken(proposalId, businessId);
          const url = await buildProposalCustomerLink(tokenData, businessId);
          setProposalUrl(url);

          const text = await loadProposalSendMessage({
            businessId,
            channel: 'sms',
            firstName: customerFirstName,
            url,
            proposalTitle,
            businessName,
          });
          setMessage(text);
        } catch (error) {
          console.error('Error generating proposal URL:', error);
          const text = await loadProposalSendMessage({
            businessId,
            channel: 'sms',
            firstName: customerFirstName,
            proposalTitle,
            businessName,
          });
          setMessage(text);
        }
      };
      
      generateUrl();
    }
  }, [isOpen, proposalTitle, businessName, customerFirstName, proposalId, businessId]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setMessage('');
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    try {
      setLoading(true);
      await onSend(message);
      onClose();
    } catch (error) {
      console.error('Error sending SMS:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      } z-[100140]`}
      style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className={`bg-white transform transition-all duration-200 fixed inset-0 w-full h-full sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[600px] sm:h-auto shadow-2xl ${
          isOpen ? 'translate-y-0 sm:translate-y-0 sm:translate-x-0 opacity-100' : 'translate-y-full sm:translate-y-0 sm:translate-x-full opacity-0 sm:opacity-100'
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
        onTransitionEnd={handleTransitionEnd}
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button - offset for safe area */}
          <button
            onClick={onClose}
            className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ 
              top: 'calc(1rem + env(safe-area-inset-top, 0px))',
              WebkitTapHighlightColor: 'transparent' 
            }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center" style={{
            paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))'
          }}>
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Send SMS
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto bg-white" style={{ 
            WebkitOverflowScrolling: 'touch',
          }}>
            <div className="p-4 sm:p-6 space-y-6">
              {/* Recipient */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  To
                </label>
                <div className="px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700">
                  {customerPhone}
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  rows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none touch-manipulation"
                  style={{ 
                    fontSize: '16px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  placeholder="Enter your message"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    You can edit this message for this send only. The default message will be restored next time.
                  </p>
                  <p className="text-xs text-gray-400">
                    {message.length} characters
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4" style={{
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          }}>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={loading || !message.trim()}
                className={`flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                  (!loading && message.trim())
                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="h-4 w-4 mr-2" />
                    Send SMS
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
